/**
 * APTableRow Component
 *
 * Table row component that handles row interactions, selection states, and custom row rendering.
 * Manages individual cells and provides click handlers for row-level actions.
 *
 * @example
 * ```tsx
 * <APTableRow
 *   row={{ id: 1, data: { name: 'John', email: 'john@example.com' } }}
 *   columns={[
 *     { key: 'name', label: 'Name', accessor: 'name' },
 *     { key: 'email', label: 'Email', accessor: 'email' }
 *   ]}
 *   rowIndex={0}
 *   onRowClick={(row) => console.log('Clicked:', row)}
 * />
 * ```
 */

import { forwardRef, useCallback } from 'react';

import type { IAPTableRowProps } from '../../types';

import APTableCell from '../cell';

import './styles.scss';

/**
 * APTableRow - Table row with cell management and interactions
 *
 * Handles row-level interactions, selection states, and renders all
 * visible cells for the row data.
 */
export const APTableRow = forwardRef<HTMLTableRowElement, IAPTableRowProps>(
  <TData,>(
    {
      row,
      columns,
      rowIndex,
      selection,
      onRowClick,
      onRowDoubleClick,
      className = '',
      'data-testid': testId = 'ap-table-row',
      ...rest
    }: IAPTableRowProps<TData> & { 'data-testid'?: string },
    ref: React.Ref<HTMLTableRowElement>
  ) => {
    const handleRowClick = useCallback(
      (event: React.MouseEvent) => {
        // Don't trigger row click if clicking on interactive elements
        if ((event.target as Element).closest('button, a, [role="button"]')) {
          return;
        }
        onRowClick?.(row, rowIndex);
      },
      [row, rowIndex, onRowClick]
    );

    const handleRowDoubleClick = useCallback(() => {
      onRowDoubleClick?.(row, rowIndex);
    }, [row, rowIndex, onRowDoubleClick]);

    // Build row classes
    const rowClasses = [
      'ap-table__row',
      rowIndex % 2 === 0 ? 'ap-table__row--even' : 'ap-table__row--odd',
      row.disabled && 'ap-table__row--disabled',
      row.selected && 'ap-table__row--selected',
      (onRowClick || onRowDoubleClick) && 'ap-table__row--clickable',
      row.className,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Custom row renderer
    if (row.render) {
      return <>{row.render(row, columns)}</>;
    }

    // Filter visible columns
    const visibleColumns = columns.filter((column) => column.visible !== false);

    return (
      <tr
        ref={ref}
        className={rowClasses}
        onClick={handleRowClick}
        onDoubleClick={handleRowDoubleClick}
        data-testid={testId}
        {...rest}
      >
        {/* Selection column */}
        {selection !== undefined &&
          selection?.type !== 'none' &&
          selection?.showCheckboxes !== false && (
            <APTableCell
              column={{
                key: '__selection__',
                label: '',
                accessor: () => null,
                align: 'center',
                className: 'ap-table__cell--selection',
              }}
              row={row}
              rowIndex={rowIndex}
              value={null}
            />
          )}

        {/* Data columns */}
        {visibleColumns.map((column) => (
          <APTableCell
            key={column.key}
            column={column}
            row={row}
            rowIndex={rowIndex}
            value={undefined} // Let APTableCell calculate the value
          />
        ))}
      </tr>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableRowProps<TData> & {
    ref?: React.Ref<HTMLTableRowElement>;
    'data-testid'?: string;
  }
) => React.ReactElement;

export default APTableRow;
